import React, { useState } from 'react';

interface TermsPrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'terms' | 'privacy';
}

const TermsPrivacyModal: React.FC<TermsPrivacyModalProps> = ({ isOpen, onClose, initialTab = 'terms' }) => {
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>(initialTab);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header con tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('terms')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'terms'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Términos y Condiciones
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'privacy'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Política de Privacidad
            </button>
          </div>
        </div>

        {/* Contenido scrolleable */}
        <div className="overflow-y-auto flex-1 p-6 text-sm text-gray-700 leading-relaxed">
          {activeTab === 'terms' ? (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900">Términos y Condiciones de Uso</h2>
              <p className="text-xs text-gray-500">Última actualización: Marzo 2026</p>

              <h3 className="font-semibold text-gray-800">1. Aceptación de los Términos</h3>
              <p>
                Al utilizar la plataforma consultAR (en adelante, "la Plataforma"), ya sea como paciente o profesional,
                usted acepta estos Términos y Condiciones en su totalidad. Si no está de acuerdo, no utilice la Plataforma.
              </p>

              <h3 className="font-semibold text-gray-800">2. Descripción del Servicio</h3>
              <p>
                consultAR es una plataforma de gestión de turnos y comunicación entre profesionales de la salud/estética
                y sus pacientes. La Plataforma facilita la reserva de citas, gestión de historias clínicas y comunicación,
                pero no provee servicios médicos ni de salud directamente.
              </p>

              <h3 className="font-semibold text-gray-800">3. Registro y Reserva de Turnos</h3>
              <p>
                Los pacientes pueden reservar turnos proporcionando sus datos personales. Al hacerlo, usted declara que
                la información proporcionada es veraz y actualizada. Los turnos están sujetos a disponibilidad y a la
                confirmación del profesional.
              </p>

              <h3 className="font-semibold text-gray-800">4. Cancelaciones</h3>
              <p>
                Las políticas de cancelación son definidas por cada profesional. Le recomendamos consultar las condiciones
                específicas al momento de agendar su turno. consultAR no se responsabiliza por cancelaciones realizadas
                por los profesionales.
              </p>

              <h3 className="font-semibold text-gray-800">5. Responsabilidad</h3>
              <p>
                consultAR actúa como intermediario tecnológico. No somos responsables por la calidad de los servicios
                prestados por los profesionales, ni por diagnósticos, tratamientos o resultados médicos. La relación
                profesional-paciente es independiente de la Plataforma.
              </p>

              <h3 className="font-semibold text-gray-800">6. Propiedad Intelectual</h3>
              <p>
                Todo el contenido de la Plataforma (diseño, logos, textos, código) es propiedad de consultAR y está
                protegido por las leyes de propiedad intelectual aplicables.
              </p>

              <h3 className="font-semibold text-gray-800">7. Modificaciones</h3>
              <p>
                Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios serán efectivos
                desde su publicación en la Plataforma. El uso continuado implica la aceptación de los términos actualizados.
              </p>

              <h3 className="font-semibold text-gray-800">8. Jurisdicción</h3>
              <p>
                Estos términos se rigen por las leyes de la República Argentina. Cualquier controversia será sometida a
                los tribunales competentes de la ciudad de Corrientes, Argentina.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900">Política de Privacidad</h2>
              <p className="text-xs text-gray-500">Última actualización: Marzo 2026</p>

              <h3 className="font-semibold text-gray-800">1. Datos que Recopilamos</h3>
              <p>Recopilamos los siguientes datos personales cuando usted utiliza la Plataforma:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Nombre completo, email y teléfono</li>
                <li>Datos de salud proporcionados voluntariamente (DNI, obra social, número de afiliado, fecha de nacimiento, domicilio)</li>
                <li>Información clínica registrada por el profesional en su historia clínica</li>
                <li>Datos de navegación y uso de la Plataforma</li>
              </ul>

              <h3 className="font-semibold text-gray-800">2. Finalidad del Tratamiento</h3>
              <p>Sus datos personales son utilizados exclusivamente para:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Gestionar la reserva y administración de turnos</li>
                <li>Facilitar la comunicación entre usted y el profesional (recordatorios por email y WhatsApp)</li>
                <li>Permitir al profesional llevar un registro clínico de sus consultas</li>
                <li>Mejorar la calidad del servicio de la Plataforma</li>
              </ul>

              <h3 className="font-semibold text-gray-800">3. Datos Sensibles de Salud</h3>
              <p>
                Los datos de salud que usted proporcione (información clínica, antecedentes, estudios) son tratados
                con especial protección conforme a la Ley 25.326 de Protección de Datos Personales de Argentina.
                Estos datos son accesibles únicamente por el profesional con quien usted tiene la relación asistencial.
              </p>

              <h3 className="font-semibold text-gray-800">4. Compartición de Datos</h3>
              <p>
                No vendemos, alquilamos ni compartimos sus datos personales con terceros, salvo:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Con el profesional con quien usted agenda un turno (para la prestación del servicio)</li>
                <li>Cuando sea requerido por ley o autoridad judicial competente</li>
                <li>Proveedores de servicios técnicos necesarios para operar la Plataforma (hosting, email), bajo acuerdos de confidencialidad</li>
              </ul>

              <h3 className="font-semibold text-gray-800">5. Seguridad</h3>
              <p>
                Implementamos medidas de seguridad técnicas y organizativas para proteger sus datos contra acceso
                no autorizado, pérdida o alteración. Las comunicaciones se realizan mediante conexiones cifradas (HTTPS).
              </p>

              <h3 className="font-semibold text-gray-800">6. Sus Derechos</h3>
              <p>
                Conforme a la Ley 25.326, usted tiene derecho a:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Acceder a sus datos personales</li>
                <li>Solicitar la rectificación de datos inexactos</li>
                <li>Solicitar la supresión de sus datos</li>
                <li>Oponerse al tratamiento de sus datos</li>
              </ul>
              <p>
                Para ejercer estos derechos, contáctenos a través de los canales de soporte de la Plataforma.
              </p>

              <h3 className="font-semibold text-gray-800">7. Conservación de Datos</h3>
              <p>
                Sus datos serán conservados mientras mantenga una relación activa con la Plataforma y durante el
                período que la legislación vigente requiera. Los datos clínicos se conservan conforme a las normativas
                de salud aplicables.
              </p>

              <h3 className="font-semibold text-gray-800">8. Contacto</h3>
              <p>
                Para consultas sobre privacidad, puede contactarnos a través del botón de soporte disponible en la Plataforma.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsPrivacyModal;
